import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  HelpCircle, Trophy, Target, Clock, TrendingUp, 
  CheckCircle2, XCircle, ChevronRight, Award, BarChart2
} from 'lucide-react';
import axios from 'axios';
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function QuizHistory({ courseId, courseTitle }) {
  const [quizHistory, setQuizHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('faheem_token');

  useEffect(() => {
    if (token) {
      loadQuizHistory();
    } else {
      setLoading(false);
    }
  }, [courseId, token]);

  const loadQuizHistory = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const url = courseId 
        ? `${BACKEND_URL}/api/topic-quiz/history?access_token=${token}&course_id=${courseId}`
        : `${BACKEND_URL}/api/topic-quiz/history?access_token=${token}`;
      
      const response = await axios.get(url);
      
      if (response.data) {
        // Check if there was an auth error returned in the response
        if (response.data.error) {
          console.log('Quiz history auth issue:', response.data.error);
        }
        setQuizHistory(response.data);
      }
    } catch (error) {
      console.error('Failed to load quiz history:', error);
      // Don't show error, just show empty state
      setQuizHistory({ history: {}, total_attempts: 0 });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreBadge = (percentage) => {
    if (percentage >= 90) return { text: 'Excellent', color: 'bg-green-600' };
    if (percentage >= 80) return { text: 'Great', color: 'bg-green-500' };
    if (percentage >= 70) return { text: 'Good', color: 'bg-yellow-500' };
    if (percentage >= 60) return { text: 'Pass', color: 'bg-yellow-600' };
    return { text: 'Needs Work', color: 'bg-red-500' };
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <HelpCircle className="w-5 h-5 text-slate-600" />
            Quiz History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <XCircle className="w-12 h-12 text-red-300 mx-auto mb-3" />
            <p className="text-slate-500">{error}</p>
            <button 
              onClick={loadQuizHistory}
              className="mt-3 text-sm text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!quizHistory || quizHistory.total_attempts === 0) {
    return (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <HelpCircle className="w-5 h-5 text-slate-600" />
            Quiz History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No quiz attempts yet</p>
            <p className="text-sm text-slate-400 mt-1">Take quizzes on course topics to track your progress</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const history = quizHistory.history;
  const courseIds = Object.keys(history);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <HelpCircle className="w-5 h-5 text-slate-600" />
            Quiz History
          </CardTitle>
          <Badge variant="outline" className="text-slate-600">
            {quizHistory.total_attempts} total attempts
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" className="space-y-2">
          {courseIds.map((cId) => {
            const courseData = history[cId];
            const topics = Object.values(courseData.topics);
            const totalAttempts = topics.reduce((sum, t) => sum + t.attempts.length, 0);
            const avgBestScore = topics.length > 0 
              ? Math.round(topics.reduce((sum, t) => sum + t.best_score, 0) / topics.length)
              : 0;

            return (
              <AccordionItem 
                key={cId} 
                value={cId}
                className="border border-slate-200 rounded-lg px-4 bg-white"
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <BarChart2 className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-slate-900">{cId}</h3>
                        <p className="text-sm text-slate-500">
                          {topics.length} topics • {totalAttempts} attempts
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${avgBestScore >= 70 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        Avg Best: {avgBestScore}%
                      </Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3">
                    {topics.map((topic) => (
                      <div 
                        key={topic.topic_id}
                        className="border border-slate-100 rounded-lg p-4 bg-slate-50"
                      >
                        {/* Topic Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-slate-900">{topic.topic_name}</h4>
                            <p className="text-xs text-slate-500 mt-1">
                              {topic.attempts.length} attempt{topic.attempts.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="flex items-center gap-1">
                                <Trophy className="w-4 h-4 text-yellow-500" />
                                <span className="font-semibold text-slate-900">{topic.best_score}%</span>
                              </div>
                              <p className="text-xs text-slate-500">Best Score</p>
                            </div>
                            <div className="text-right ml-4">
                              <div className="flex items-center gap-1">
                                <Target className="w-4 h-4 text-blue-500" />
                                <span className="font-semibold text-slate-900">{topic.average_score}%</span>
                              </div>
                              <p className="text-xs text-slate-500">Average</p>
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Progress to Mastery</span>
                            <span>{topic.best_score >= 80 ? 'Mastered!' : `${80 - topic.best_score}% to go`}</span>
                          </div>
                          <Progress 
                            value={Math.min(topic.best_score, 100)} 
                            className="h-2"
                          />
                        </div>

                        {/* Attempt History */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Recent Attempts</p>
                          <div className="space-y-1">
                            {topic.attempts.slice(0, 5).map((attempt, idx) => {
                              const badge = getScoreBadge(attempt.percentage);
                              return (
                                <div 
                                  key={attempt.id}
                                  className="flex items-center justify-between py-2 px-3 bg-white rounded border border-slate-100"
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs flex items-center justify-center font-medium">
                                      #{topic.attempts.length - idx}
                                    </span>
                                    <div className="flex items-center gap-1 text-slate-500 text-xs">
                                      <Clock className="w-3 h-3" />
                                      {formatDate(attempt.created_at)}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-600">
                                      {attempt.score}/{attempt.total}
                                    </span>
                                    <Badge className={`${badge.color} text-white text-xs`}>
                                      {attempt.percentage}%
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {topic.attempts.length > 5 && (
                            <p className="text-xs text-slate-400 text-center">
                              +{topic.attempts.length - 5} more attempts
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}
